'use client';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useState } from 'react';

import { PropsWithClassname } from '@/app/types';
import { Reward } from '@/app/api';

import RewardBox from '@/app/components/RewardBox/RewardBox';
import RewardPickerDrawer from '@/app/components/RewardPickerDrawer/RewardPickerDrawer';
import { useIsProjectActive } from '@/app/providers/ProjectProvider';

interface RewardPickerProps extends PropsWithClassname {
  rewards: Reward[];
  selectedRewardId?: string;
  selectedRewardVariantId?: string;
  label: string;
  projectNumericId: number;
}

const RewardPicker = ({
  rewards,
  selectedRewardId,
  selectedRewardVariantId,
  label,
  className,
  projectNumericId,
}: RewardPickerProps) => {
  const { t } = useTranslation();
  const [isRewardDrawerOpen, setIsRewardDrawerOpen] = useState(false);
  const isProjectActive = useIsProjectActive();

  const handleChangeClick = useCallback(() => {
    document.body.style.overflow = 'hidden';
    setIsRewardDrawerOpen(true);
  }, [setIsRewardDrawerOpen]);

  const handleRewardDrawerClose = useCallback(() => {
    document.body.style.overflow = 'auto';
    setIsRewardDrawerOpen(false);
  }, [setIsRewardDrawerOpen]);

  const selectedReward = useMemo(() => {
    if (!selectedRewardId) {
      return null;
    }

    const selectedReward = rewards.find((reward) => reward.id === selectedRewardId);

    if (!selectedReward) {
      return null;
    }

    return selectedReward;
  }, [rewards, selectedRewardId]);

  const selectedRewardVariant = useMemo(() => {
    if (!selectedReward?.variants?.length) {
      return null;
    }

    const selectedVariant = selectedReward.variants.find((variant) => variant.id === selectedRewardVariantId);

    if (!selectedVariant) {
      return null;
    }

    return selectedVariant;
  }, [selectedReward]);

  return (
    <>
      <div className={classNames(className, '')}>
        <div className="flex justify-between mb-[12px] text-[10px] lg:text-[12px] font-medium uppercase tracking-[3px] leading-[24px] lg:tracking-[4.5px]">
          <span className="block uppercase">{label}</span>
          <span className="text-primary-mid uppercase cursor-pointer" onClick={handleChangeClick}>
            {t('components.RewardPicker.change')}
          </span>
        </div>
        {!!selectedReward && (
          <>
            <div className="xl:hidden">
              <RewardBox
                shouldHideControlButtons={!isProjectActive}
                reward={selectedReward}
                selectedVariant={selectedRewardVariant ?? undefined}
                idPrefix="selected"
                isImageAlwaysVisible
              />
            </div>
            <div className="hidden xl:block">
              <RewardBox
                shouldHideControlButtons={!isProjectActive}
                reward={selectedReward}
                selectedVariant={selectedRewardVariant ?? undefined}
                idPrefix="selected"
              />
            </div>
          </>
        )}
      </div>
      {!!selectedReward && (
        <RewardPickerDrawer
          numericId={projectNumericId}
          isOpen={isRewardDrawerOpen}
          onClose={handleRewardDrawerClose}
          rewards={rewards}
          selectedVariant={selectedRewardVariant ?? undefined}
        />
      )}
    </>
  );
};

export default RewardPicker;
